import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTA } from '../components/CTA';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { SEO, BreadcrumbSchema } from '../components/SEO';

export default function ServicesPage() {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' }
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      <SEO 
        title="Services - Appointment Generation for Pool, Kitchen, Bathroom, Roofing, HVAC Contractors"
        description="RenoNovo generates exclusive appointments for home service contractors: pool builders, kitchen remodeling, bathroom remodeling, roofing, HVAC, tile & flooring, backyard renovations, and general contractors."
        keywords="pool builder leads, kitchen remodeling leads, bathroom remodeling leads, roofing leads, HVAC leads, contractor appointments, home service leads"
        canonical="/services"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-24 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
              Industries We Serve
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0f172a] tracking-tight leading-tight mb-6">
              Appointment Generation for<br />
              <span className="text-[#2563eb]">Home Service Contractors</span>
            </h1>
            <p className="text-lg text-[#64748b] leading-relaxed max-w-3xl mx-auto mb-8">
              We specialize in connecting qualified homeowners with trusted contractors across 
              all major home improvement categories.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-8 h-14 rounded-sm text-base"
                data-testid="services-hero-cta"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Pool Builders */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src="https://images.unsplash.com/photo-1694782174671-279cb163ddb8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Pool Builders"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-sm">
                  <p className="text-xs text-[#64748b]">Avg. Project Value</p>
                  <p className="text-lg font-bold text-[#0f172a]">$45,000 - $120,000</p>
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#0f172a] mb-4">Pool Builders</h2>
                <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                  Connect with homeowners ready to build their dream pools and outdoor living spaces.
                </p>
                <p className="text-sm font-semibold text-[#0f172a] uppercase tracking-wide mb-3">Services We Target:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Inground Pool Construction</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Pool Renovation</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Pool Deck & Patio</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Hot Tub Installation</span></div>
                </div>
                <Link to="/contact"><Button variant="outline" className="bg-white text-[#0f172a] font-semibold px-6 h-12 rounded-sm border-2 border-[#0f172a]/10">Get Pool Builder Appointments <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              </div>
            </div>
          </div>
        </section>

        {/* Kitchen Remodeling */}
        <section className="py-20 md:py-24 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2 relative overflow-hidden rounded-sm">
                <img
                  src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Kitchen Remodeling"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-sm">
                  <p className="text-xs text-[#64748b]">Avg. Project Value</p>
                  <p className="text-lg font-bold text-[#0f172a]">$25,000 - $75,000</p>
                </div>
              </div>
              <div className="lg:order-1">
                <h2 className="text-3xl font-bold text-[#0f172a] mb-4">Kitchen Remodeling</h2>
                <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                  Qualified leads for complete kitchen transformations and upgrades.
                </p>
                <p className="text-sm font-semibold text-[#0f172a] uppercase tracking-wide mb-3">Services We Target:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Full Kitchen Renovation</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Cabinet Refacing</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Countertop Installation</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Kitchen Island Design</span></div>
                </div>
                <Link to="/contact"><Button variant="outline" className="bg-white text-[#0f172a] font-semibold px-6 h-12 rounded-sm border-2 border-[#0f172a]/10">Get Kitchen Remodeling Appointments <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              </div>
            </div>
          </div>
        </section>

        {/* Bathroom Remodeling */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src="https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Bathroom Remodeling"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-sm">
                  <p className="text-xs text-[#64748b]">Avg. Project Value</p>
                  <p className="text-lg font-bold text-[#0f172a]">$15,000 - $50,000</p>
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#0f172a] mb-4">Bathroom Remodeling</h2>
                <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                  Appointments with homeowners seeking bathroom upgrades and renovations.
                </p>
                <p className="text-sm font-semibold text-[#0f172a] uppercase tracking-wide mb-3">Services We Target:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Full Bath Renovation</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Walk-in Shower Conversion</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Vanity Upgrades</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Accessibility Mods</span></div>
                </div>
                <Link to="/contact"><Button variant="outline" className="bg-white text-[#0f172a] font-semibold px-6 h-12 rounded-sm border-2 border-[#0f172a]/10">Get Bathroom Remodeling Appointments <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              </div>
            </div>
          </div>
        </section>

        {/* Roofing */}
        <section className="py-20 md:py-24 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2 relative overflow-hidden rounded-sm">
                <img
                  src="https://images.unsplash.com/photo-1726589004565-bedfba94d3a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Roofing Services"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-sm">
                  <p className="text-xs text-[#64748b]">Avg. Project Value</p>
                  <p className="text-lg font-bold text-[#0f172a]">$8,000 - $35,000</p>
                </div>
              </div>
              <div className="lg:order-1">
                <h2 className="text-3xl font-bold text-[#0f172a] mb-4">Roofing Services</h2>
                <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                  Connect with property owners needing roof repairs, replacements, and inspections.
                </p>
                <p className="text-sm font-semibold text-[#0f172a] uppercase tracking-wide mb-3">Services We Target:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Roof Replacement</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Storm Damage Repair</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Roof Inspections</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Gutter Installation</span></div>
                </div>
                <Link to="/contact"><Button variant="outline" className="bg-white text-[#0f172a] font-semibold px-6 h-12 rounded-sm border-2 border-[#0f172a]/10">Get Roofing Appointments <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              </div>
            </div>
          </div>
        </section>

        {/* HVAC */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src="https://images.pexels.com/photos/5463581/pexels-photo-5463581.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="HVAC Services"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-sm">
                  <p className="text-xs text-[#64748b]">Avg. Project Value</p>
                  <p className="text-lg font-bold text-[#0f172a]">$5,000 - $25,000</p>
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#0f172a] mb-4">HVAC Services</h2>
                <p className="text-lg text-[#64748b] leading-relaxed mb-6">
                  Service calls from homeowners needing heating and cooling solutions.
                </p>
                <p className="text-sm font-semibold text-[#0f172a] uppercase tracking-wide mb-3">Services We Target:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">System Replacement</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">AC Installation</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Furnace Installation</span></div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#2563eb]" /><span className="text-[#64748b]">Ductwork Services</span></div>
                </div>
                <Link to="/contact"><Button variant="outline" className="bg-white text-[#0f172a] font-semibold px-6 h-12 rounded-sm border-2 border-[#0f172a]/10">Get HVAC Appointments <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
              </div>
            </div>
          </div>
        </section>

        {/* More Industries Section */}
        <section className="py-20 md:py-24 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#0f172a] mb-4">More Industries We Serve</h2>
              <p className="text-[#64748b] max-w-2xl mx-auto">
                We also generate appointments for these additional home service categories.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-sm border border-[#e2e8f0]">
                <h3 className="text-xl font-bold text-[#0f172a] mb-3">Backyard Renovations</h3>
                <p className="text-[#64748b] mb-4">Outdoor living spaces, patios, landscaping, and more.</p>
                <p className="text-sm text-[#2563eb] font-semibold">$20,000 - $80,000 avg. project</p>
              </div>
              <div className="bg-white p-8 rounded-sm border border-[#e2e8f0]">
                <h3 className="text-xl font-bold text-[#0f172a] mb-3">Tile & Flooring</h3>
                <p className="text-[#64748b] mb-4">Hardwood, tile, LVP, and natural stone installations.</p>
                <p className="text-sm text-[#2563eb] font-semibold">$5,000 - $25,000 avg. project</p>
              </div>
              <div className="bg-white p-8 rounded-sm border border-[#e2e8f0]">
                <h3 className="text-xl font-bold text-[#0f172a] mb-3">General Contractors</h3>
                <p className="text-[#64748b] mb-4">Full home renovations, additions, and construction.</p>
                <p className="text-sm text-[#2563eb] font-semibold">$50,000 - $250,000+ avg. project</p>
              </div>
            </div>
          </div>
        </section>

        {/* Don't See Your Industry */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <h3 className="text-2xl font-bold text-[#0f172a] mb-4">Don't See Your Industry?</h3>
            <p className="text-[#64748b] mb-6 max-w-2xl mx-auto">
              We work with a wide variety of home service contractors. Reach out — we likely can help.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-8 h-12 rounded-sm"
                data-testid="services-contact-cta"
              >
                Contact Us to Discuss
              </Button>
            </Link>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
